
/**
 * Centralized logging utility to standardize logging across the application
 * and provide runtime control over log levels
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

interface LoggerOptions {
  prefix?: string;
  module?: string;
  enabled?: boolean;
}

interface LogCount {
  debug: number;
  info: number;
  warn: number;
  error: number;
  total: number;
}

class Logger {
  private static instance: Logger;
  private logLevel: LogLevel = 'info';
  private rateLimits: Map<string, number> = new Map();
  private logCounts: LogCount = { debug: 0, info: 0, warn: 0, error: 0, total: 0 };
  
  // Default rate limit settings (ms)
  private defaultRateLimits = {
    debug: 5000,  // 5 seconds between duplicate debug logs
    info: 2000,   // 2 seconds between duplicate info logs
    warn: 1000,   // 1 second between duplicate warning logs
    error: 0      // No rate limiting for errors
  };

  private constructor() {
    // Set initial log level based on environment
    this.logLevel = process.env.NODE_ENV === 'development' 
      ? 'debug' 
      : 'warn';
      
    // Monitor for excessive logging
    this.monitorLogVolume();
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }
  
  // Set the global log level
  public setLevel(level: LogLevel): void {
    this.logLevel = level;
    console.log(`🔊 Log level set to: ${level}`);
  }

  // Get a new logger with specific options
  public getLogger(options: LoggerOptions = {}): {
    debug: (message: any, ...args: any[]) => void;
    info: (message: any, ...args: any[]) => void;
    warn: (message: any, ...args: any[]) => void;
    error: (message: any, ...args: any[]) => void;
  } {
    const prefix = options.prefix || '';
    const module = options.module ? `[${options.module}]` : '';
    const enabled = options.enabled !== undefined ? options.enabled : true;
    
    return {
      debug: (message: any, ...args: any[]) => {
        if (!enabled || !this.shouldLog('debug')) return;
        if (this.isRateLimited('debug', `${prefix}${module}${message}`)) return;
        this.logCounts.debug++;
        this.logCounts.total++;
        console.debug(`${prefix}${module}`, message, ...args);
      },
      
      info: (message: any, ...args: any[]) => {
        if (!enabled || !this.shouldLog('info')) return;
        if (this.isRateLimited('info', `${prefix}${module}${message}`)) return;
        this.logCounts.info++;
        this.logCounts.total++;
        console.info(`${prefix}${module}`, message, ...args);
      },
      
      warn: (message: any, ...args: any[]) => {
        if (!enabled || !this.shouldLog('warn')) return;
        if (this.isRateLimited('warn', `${prefix}${module}${message}`)) return;
        this.logCounts.warn++;
        this.logCounts.total++;
        console.warn(`${prefix}${module}`, message, ...args);
      },
      
      error: (message: any, ...args: any[]) => {
        if (!enabled) return;
        this.logCounts.error++;
        this.logCounts.total++;
        console.error(`${prefix}${module}`, message, ...args);
      }
    };
  }
  
  // Check if we should log based on current level
  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error', 'none'];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const messageLevelIndex = levels.indexOf(level);
    
    return messageLevelIndex >= currentLevelIndex;
  }
  
  // Check if a log message is rate-limited
  private isRateLimited(level: LogLevel, message: string): boolean {
    const now = Date.now();
    const key = `${level}:${message}`;
    const lastLog = this.rateLimits.get(key) || 0;
    const limitMs = this.defaultRateLimits[level as keyof typeof this.defaultRateLimits];
    
    if (limitMs && now - lastLog < limitMs) {
      return true; // Rate limited
    }
    
    this.rateLimits.set(key, now);
    return false;
  }
  
  // Monitor for excessive logging and auto-adjust
  private monitorLogVolume(): void {
    // Check log volume every 10 seconds
    setInterval(() => {
      const {total, debug} = this.logCounts;
      
      // If we're logging too much, auto-adjust level to reduce volume
      if (total > 1000 && this.logLevel === 'debug') {
        console.warn('⚠️ Excessive logging detected, reducing log level to info');
        this.logLevel = 'info';
      } else if (total > 2000 && this.logLevel === 'info') {
        console.warn('⚠️ Excessive logging detected, reducing log level to warn');
        this.logLevel = 'warn';
      }
      
      // Reset counters
      this.logCounts = { debug: 0, info: 0, warn: 0, error: 0, total: 0 };
    }, 10000);
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Export convenience function to get a configured logger
export const getLogger = (options: LoggerOptions = {}) => logger.getLogger(options);

// Example usage:
// const log = getLogger({ module: 'BulletinImage' });
// log.info('Image loaded:', { url: '...' });
